import { IAccount, IReclassesAccounts, IReclassificationAccountRef } from '@modeso/types__aumico-lib-annualreport-be';

/**
 * Composite-key utilities for resolving reclassification transaction account
 * references against the AR's current account mapping.
 *
 * Account numbers are NOT guaranteed unique inside an annual report (see
 * `IDuplicateAccountResolution`). `accountNumber` + `name` together form the
 * base lookup key.
 *
 * When an account is a **true duplicate** (same `accountNumber` AND same
 * `name` as another account in the same statement), the system assigns a
 * stable `uniqueAccountId` UUID to each instance. The triple key
 * `(accountNumber, name, uniqueAccountId)` then becomes the authoritative
 * identity and is used throughout the engine.
 *
 * Accounts that are NOT true duplicates never have a `uniqueAccountId`; they
 * continue to use the 2-part `accountNumber|name` key — no regression.
 */

type AccountLike = Pick<IAccount, 'accountNumber' | 'name'> | Pick<IReclassesAccounts, 'accountNumber' | 'name'>;
type AccountLikeWithId = AccountLike & { uniqueAccountId?: string };

/**
 * Build the composite key for a transaction ref.
 *
 * - When `uniqueAccountId` is present: `${accountNumber}|${name}|${uniqueAccountId}`
 * - Otherwise:                          `${accountNumber}|${name}`
 */
export function keyOf(ref: IReclassificationAccountRef): string {
    return ref.uniqueAccountId
        ? `${ref.accountNumber}|${ref.name}|${ref.uniqueAccountId}`
        : `${ref.accountNumber}|${ref.name}`;
}

/** Build the composite key for an account (real or synthetic reclass row). */
export function keyOfAccount(acc: AccountLikeWithId): string {
    return (acc as AccountLikeWithId).uniqueAccountId
        ? `${acc.accountNumber}|${acc.name ?? ''}|${(acc as AccountLikeWithId).uniqueAccountId}`
        : `${acc.accountNumber}|${acc.name ?? ''}`;
}

/** Convenience: build a fresh ref from an account. */
export function refFromAccount(acc: AccountLikeWithId): IReclassificationAccountRef {
    return {
        accountNumber: acc.accountNumber,
        name: acc.name ?? '',
        uniqueAccountId: (acc as AccountLikeWithId).uniqueAccountId,
    };
}

/**
 * Resolve a transaction ref against a list of accounts.
 *
 * Rules (in priority order):
 *  1. If the ref carries a `uniqueAccountId`, find the account whose
 *     `uniqueAccountId` matches exactly (ignores `accountNumber`/`name`
 *     beyond confirming the UUID, since the UUID alone is globally unique
 *     within the report).
 *  2. Exact composite `(accountNumber, name)` match among accounts WITHOUT a
 *     `uniqueAccountId` — returns that account if exactly 1 match.
 *  3. If exact match is ambiguous (>1 matches without IDs), the ref is
 *     unresolvable → returns undefined.
 *  4. Fallback: if exactly one account shares the `accountNumber` (regardless
 *     of name), return it — handles ARs where a duplicate was renamed after
 *     the transaction was created.
 *  5. Otherwise undefined → caller treats as conflict.
 */
export function resolveRef<T extends AccountLikeWithId>(
    ref: IReclassificationAccountRef,
    accounts: T[],
): T | undefined {
    // Rule 1: UUID-first match for disambiguated duplicates.
    if (ref.uniqueAccountId) {
        const byId = accounts.find((a) => (a as AccountLikeWithId).uniqueAccountId === ref.uniqueAccountId);
        if (byId != null) return byId;
        // UUID not found — fall through to name-based rules for resilience.
    }

    // Rule 2 & 3: exact 2-part match (only among accounts without a uniqueAccountId
    // so we don't conflate a named duplicate with an un-named one).
    const exact = accounts.filter(
        (a) =>
            a.accountNumber === ref.accountNumber &&
            (a.name ?? '') === ref.name &&
            !(a as AccountLikeWithId).uniqueAccountId,
    );
    if (exact.length === 1) return exact[0];
    if (exact.length > 1) return undefined; // genuinely ambiguous

    // Rule 4: unique-by-number fallback.
    const byNumber = accounts.filter((a) => a.accountNumber === ref.accountNumber);
    if (byNumber.length === 1) return byNumber[0];

    return undefined;
}

/**
 * Two refs are equal iff they refer to the same logical account.
 *
 * - When BOTH carry `uniqueAccountId`: compare UUIDs only.  Different UUIDs
 *   means different instances of a true-duplicate account pair — do NOT fall
 *   back to `accountNumber` + `name`, which is identical for both instances
 *   and would cause both to match the same transaction.
 * - When ONLY ONE carries `uniqueAccountId`: they are NOT equal.  Once an
 *   account is disambiguated, we must never fall back to `accountNumber` +
 *   `name`, otherwise duplicate siblings would both match and reclasses would
 *   leak across rows.
 * - When NEITHER carries `uniqueAccountId`: compare `accountNumber` + `name`.
 */
export function refsEqual(a: IReclassificationAccountRef, b: IReclassificationAccountRef): boolean {
    if (a.uniqueAccountId && b.uniqueAccountId) return a.uniqueAccountId === b.uniqueAccountId;
    if (a.uniqueAccountId || b.uniqueAccountId) return false;
    return a.accountNumber === b.accountNumber && a.name === b.name;
}
